// Ombro: GIRD (déficit de rotação interna) e TROM (arco total de rotação).

const { toNumber, round } = require('./primitives');

// Acima disso o déficit de TROM é alerta, em qualquer direção.
const TROM_DEFICIT_ALERT_DEGREES = 5;
// Fim da faixa verde do GIRD: a partir daqui a assimetria invertida também é sinalizada.
const SHOULDER_ASYMMETRY_ALERT_DEGREES = 10;
const GIRD_CLINICAL_ATTENTION_DEGREES = 6;
const GIRD_SEVERE_DEGREES = 18;
// GIRD negativo não é "GIRD negativo": é diferença no sentido oposto, com mais rotação
// interna no dominante. A frase vale para a tela, o PDF, a conclusão e a ACIA, então mora
// aqui para sair igual nos quatro.
const GIRD_NO_DEFICIT_NOTE = 'Não foi identificado déficit de rotação interna no membro dominante';

// GIRD = rotação interna do membro NÃO dominante − rotação interna do dominante.
// Negativo significa que o dominante tem mais rotação: pela definição não é GIRD, e a
// classificação considera 0°.
function girdValue(info) {
    const dominant = info.dominant_limb;
    const right = toNumber(info.mobility_internal_right);
    const left = toNumber(info.mobility_internal_left);
    if (!['right', 'left'].includes(dominant) || right === null || left === null) return null;

    return round(dominant === 'right' ? left - right : right - left);
}

// Devolve null quando não há como calcular — cada consumidor escolhe seu texto ('--',
// 'Não calculado', 'Não informado').
function girdClassification(value) {
    if (value === null) return null;

    const consideredValue = Math.max(0, value);
    if (consideredValue <= 9) return 'Leve — Verde';
    if (consideredValue <= 14) return 'Médio — Amarelo';
    if (consideredValue <= 17) return 'Moderado — Laranja';
    return 'Severo — Vermelho';
}

function shoulderTrom(info, side) {
    const internal = toNumber(info[`mobility_internal_${side}`]);
    const external = toNumber(info[`mobility_external_${side}`]);
    if (internal === null || external === null) return null;

    return round(internal + external);
}

// Mesmo sentido do GIRD: arco do não dominante − arco do dominante.
function shoulderTromDeficit(info) {
    const dominant = info.dominant_limb;
    const right = shoulderTrom(info, 'right');
    const left = shoulderTrom(info, 'left');
    if (!['right', 'left'].includes(dominant) || right === null || left === null) return null;

    return round(dominant === 'right' ? left - right : right - left);
}

function shoulderTromDeficitIsRisk(deficit) {
    return deficit !== null && Math.abs(deficit) > TROM_DEFICIT_ALERT_DEGREES;
}

// Déficit relevante no lado NÃO dominante, que o GIRD sozinho não enxerga por considerar
// negativo como 0°. Devolve null quando não há nada a sinalizar.
function shoulderNonDominantAsymmetry(value, dominantLimb) {
    if (value === null || value >= 0) return null;

    const degrees = round(Math.abs(value));
    if (degrees < SHOULDER_ASYMMETRY_ALERT_DEGREES) return null;

    return {
        degrees,
        sideLabel: dominantLimb === 'right' ? 'esquerdo' : 'direito'
    };
}

// Estado clínico do par GIRD + TROM, sem texto: a tela e o relatório escrevem a mesma
// conclusão com palavras próprias, então quem decide a frase é quem exibe.
//   'reduced_arc'           arco total reduzido no dominante — possivelmente patológico
//   'reduced_arc_non_dominant'  a perda de arco está no lado não dominante
//   'preserved_arc'         GIRD severo mas arco preservado — sugere adaptação esportiva
function girdTromFinding(gird, tromDeficit) {
    if (shoulderTromDeficitIsRisk(tromDeficit)) {
        return tromDeficit > 0 ? 'reduced_arc' : 'reduced_arc_non_dominant';
    }
    if (gird !== null && gird >= GIRD_SEVERE_DEGREES) {
        return 'preserved_arc';
    }
    return null;
}

// Notas clínicas do par GIRD + TROM, na ordem em que devem ser lidas. Lista vazia = nada
// a observar; quem exibe junta como preferir.
function girdInterpretationNotes(gird, tromDeficit) {
    const notes = [];
    const finding = girdTromFinding(gird, tromDeficit);

    if (gird !== null && gird < 0) {
        notes.push(GIRD_NO_DEFICIT_NOTE);
    }

    if (finding === 'reduced_arc') {
        notes.push('Redução associada do arco total — compatível com GIRD potencialmente patológico');
    } else if (finding === 'reduced_arc_non_dominant') {
        notes.push(`Arco total reduzido no membro não dominante (${Math.abs(tromDeficit).toFixed(1)}° de diferença)`);
    } else if (finding === 'preserved_arc') {
        notes.push('Arco total preservado — possível adaptação esportiva');
    }

    const asymmetry = shoulderNonDominantAsymmetry(gird, null);
    if (asymmetry) {
        notes.push(`Assimetria de rotação interna de ${asymmetry.degrees.toFixed(1)}° com déficit no membro não dominante — não configura GIRD, mas merece correlação clínica`);
    } else if (gird !== null && gird >= GIRD_CLINICAL_ATTENTION_DEGREES) {
        notes.push('Atenção clínica a partir de 6°; até 10° pode ser aceitável conforme especificidade esportiva');
    }

    return notes;
}

module.exports = {
    TROM_DEFICIT_ALERT_DEGREES,
    SHOULDER_ASYMMETRY_ALERT_DEGREES,
    GIRD_CLINICAL_ATTENTION_DEGREES,
    GIRD_SEVERE_DEGREES,
    GIRD_NO_DEFICIT_NOTE,
    girdValue,
    girdClassification,
    shoulderTrom,
    shoulderTromDeficit,
    shoulderTromDeficitIsRisk,
    shoulderNonDominantAsymmetry,
    girdTromFinding,
    girdInterpretationNotes
};
